import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Calendar, MessageSquare, RefreshCw, Users } from 'lucide-react';
import { Alert } from '../../../shared/components/Alert';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { Button } from '../../../shared/components/Button';
import { EmptyState } from '../../../shared/components/EmptyState';
import { Skeleton } from '../../../shared/components/Skeleton';
import { Textarea } from '../../../shared/components/Textarea';
import { TextInput } from '../../../shared/components/TextInput';
import { api } from '../../../shared/lib/axios';
import type { PropostaEventoNegociacao, TipoEventoNegociacao } from '../api';
import { PROPOSAL_KEYS } from '../api';
import { useRegistrarNegociacao } from '../hooks';
import { TIPO_NEGOCIACAO_LABEL } from '../utils/format';

const TIPO_OPTIONS = (Object.keys(TIPO_NEGOCIACAO_LABEL) as TipoEventoNegociacao[]).map((t) => ({
  value: t,
  label: TIPO_NEGOCIACAO_LABEL[t],
}));

const TIPO_ICONS: Record<TipoEventoNegociacao, React.ReactNode> = {
  reuniao: <Users className="h-4 w-4" />,
  ligacao: <MessageSquare className="h-4 w-4" />,
  email: <MessageSquare className="h-4 w-4" />,
  contraproposta: <RefreshCw className="h-4 w-4" />,
  solicitacao_cliente: <MessageSquare className="h-4 w-4" />,
  alteracao: <RefreshCw className="h-4 w-4" />,
};

interface Props {
  proposalId: string;
  versionId: string;
  /** Bloqueia registro de novos eventos (versão congelada). */
  readonly?: boolean;
}

/**
 * Timeline de negociação (US-COM-006, task 16.2).
 *
 * Lista os eventos + formulário inline para registrar novos. Contraproposta
 * exige `reasonForNewVersion` (RN-COM-020) — o backend gera nova versão
 * automaticamente e amarra `generated_version_id`. Aqui a UI destaca
 * quando o evento gerou nova versão.
 */
export function NegociacaoPropostaTimeline({ proposalId, versionId, readonly }: Props) {
  const registrar = useRegistrarNegociacao(proposalId, versionId);

  const listagem = useQuery({
    queryKey: PROPOSAL_KEYS.negotiation(versionId),
    queryFn: async () => {
      const { data } = await api.get<PropostaEventoNegociacao[] | { data: PropostaEventoNegociacao[] }>(
        `/proposals/${proposalId}/versions/${versionId}/negotiation-events`,
      );
      return Array.isArray(data) ? data : data.data;
    },
    enabled: !!versionId,
  });

  const [tipo, setTipo] = useState<TipoEventoNegociacao>('reuniao');
  const [resumo, setResumo] = useState('');
  const [occurredAt, setOccurredAt] = useState(new Date().toISOString().slice(0, 16));
  const [motivoVersao, setMotivoVersao] = useState('');
  const [erro, setErro] = useState<string | null>(null);

  async function handleSubmit() {
    setErro(null);
    if (!resumo.trim()) return setErro('Resumo é obrigatório.');
    if (tipo === 'contraproposta' && !motivoVersao.trim()) {
      return setErro('Motivo da nova versão é obrigatório para contraproposta.');
    }
    try {
      await registrar.mutateAsync({
        tipo,
        resumo: resumo.trim(),
        occurredAt: new Date(occurredAt).toISOString(),
        reasonForNewVersion: motivoVersao.trim() || undefined,
      });
      setResumo('');
      setMotivoVersao('');
    } catch (e) {
      setErro(extractError(e));
    }
  }

  return (
    <div className="space-y-6">
      {!readonly && (
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <h3 className="mb-3 text-sm font-medium text-gray-900">Registrar evento</h3>
          {erro && (
            <Alert variant="error" className="mb-3">
              {erro}
            </Alert>
          )}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <AutocompleteSelect
              label="Tipo *"
              options={TIPO_OPTIONS}
              value={tipo}
              onChange={(v) => setTipo(v as TipoEventoNegociacao)}
            />
            <TextInput
              label="Data/hora *"
              type="datetime-local"
              value={occurredAt}
              onChange={(e) => setOccurredAt(e.target.value)}
            />
            <div className="md:col-span-2">
              <Textarea
                label="Resumo *"
                value={resumo}
                onChange={(e) => setResumo(e.target.value)}
                rows={2}
                maxLength={4000}
              />
            </div>
            {tipo === 'contraproposta' && (
              <div className="md:col-span-2">
                <Textarea
                  label="Motivo da nova versão * (contraproposta)"
                  value={motivoVersao}
                  onChange={(e) => setMotivoVersao(e.target.value)}
                  rows={2}
                  maxLength={2000}
                />
                <p className="mt-1 text-xs text-gray-500">
                  A versão atual será marcada como Substituída e uma nova versão nascerá em
                  Em Elaboração (RN-COM-020).
                </p>
              </div>
            )}
          </div>
          <div className="mt-3 flex justify-end">
            <Button onClick={handleSubmit} disabled={registrar.isPending}>
              {registrar.isPending ? 'Registrando…' : 'Registrar evento'}
            </Button>
          </div>
        </div>
      )}

      {listagem.isLoading && <Skeleton className="h-20 w-full" />}
      {listagem.isError && (
        <Alert variant="error">
          Erro ao carregar eventos: {(listagem.error as Error)?.message ?? 'erro'}
        </Alert>
      )}
      {!listagem.isLoading && (listagem.data ?? []).length === 0 && (
        <EmptyState
          title="Nenhum evento de negociação registrado"
          description="Reuniões, ligações, e-mails e contrapropostas aparecem aqui."
        />
      )}
      {!listagem.isLoading && (listagem.data ?? []).length > 0 && (
        <ol className="space-y-3">
          {(listagem.data ?? []).map((ev) => (
            <li
              key={ev.id}
              className="flex gap-3 rounded-lg border border-gray-200 bg-white p-3 shadow-sm"
            >
              <div
                className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ${
                  ev.tipo === 'contraproposta'
                    ? 'bg-amber-100 text-amber-700'
                    : 'bg-blue-100 text-blue-700'
                }`}
              >
                {TIPO_ICONS[ev.tipo]}
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-900">
                    {TIPO_NEGOCIACAO_LABEL[ev.tipo]}
                  </span>
                  <span className="flex items-center gap-1 text-xs text-gray-500">
                    <Calendar className="h-3 w-3" />
                    {new Date(ev.occurredAt).toLocaleString('pt-BR')}
                  </span>
                </div>
                <p className="mt-1 whitespace-pre-line text-sm text-gray-700">{ev.resumo}</p>
                {ev.generatedVersionId && (
                  <p className="mt-2 text-xs text-blue-700">
                    Gerou nova versão · id {ev.generatedVersionId.slice(0, 8)}…
                  </p>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const resp = (e as { response?: { data?: { message?: string | string[] } } }).response;
    const msg = resp?.data?.message;
    if (Array.isArray(msg)) return msg.join('; ');
    if (typeof msg === 'string') return msg;
  }
  return e instanceof Error ? e.message : 'Erro desconhecido.';
}
